import { useState, useEffect, useCallback } from 'react';
import { UserNoteResponse, UserNoteCreate, UserNoteUpdate } from '../../../types';
import { 
  fetchUserNotes, 
  createUserNote, 
  updateUserNote, 
  deleteUserNote 
} from '../../../services/api/endpoints/users';

interface UseUserNotesResult {
  notes: UserNoteResponse[];
  isLoading: boolean;
  error: string | null;
  createNote: (text: string, is_task: boolean, is_done: boolean) => Promise<void>;
  updateNote: (noteId: string, text: string, is_task: boolean, is_done: boolean) => Promise<void>;
  deleteNote: (noteId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Custom hook to manage user notes
 * Provides CRUD operations for notes on a specific user
 */
const useUserNotes = (token: string | null, userId: string | null): UseUserNotesResult => {
  const [notes, setNotes] = useState<UserNoteResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notes for the user
  const loadNotes = useCallback(async (bypassCache: boolean = false) => {
    if (!token || !userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchUserNotes(token, userId, bypassCache);
      setNotes(data);
    } catch (err) {
      console.error('Error loading user notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load notes');
      setNotes([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, userId]);

  // Create a new note
  const createNote = useCallback(async (text: string, is_task: boolean, is_done: boolean) => {
    if (!token || !userId) {
      throw new Error('Missing token or userId');
    }

    const payload: UserNoteCreate = {
      subject_id: userId,
      text,
      is_task,
      is_done

    };

    try {
      const newNote = await createUserNote(token, payload);
      // Optimistically add the note to the list
      setNotes(prev => [newNote, ...prev]);
    } catch (err) {
      console.error('Error creating note:', err);
      throw err;
    }
  }, [token, userId]);

  // Update an existing note
  const updateNote = useCallback(async (noteId: string, text: string, is_task: boolean, is_done: boolean) => {
    if (!token) {
      throw new Error('Missing token');
    }

    const payload: UserNoteUpdate = { text, is_task, is_done };

    try {
      const updatedNote = await updateUserNote(token, noteId, payload);
      // Optimistically update the note in the list
      setNotes(prev => prev.map(note => 
        note.id === noteId ? updatedNote : note
      ));
    } catch (err) {
      console.error('Error updating note:', err);
      throw err;
    }
  }, [token]);

  // Delete a note
  const deleteNote = useCallback(async (noteId: string) => {
    if (!token) {
      throw new Error('Missing token');
    }

    try {
      await deleteUserNote(token, noteId);
      // Optimistically remove the note from the list
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      throw err;
    }
  }, [token]);

  // Refresh notes (force refetch)
  const refresh = useCallback(async () => {
    await loadNotes(true);
  }, [loadNotes]);

  // Load notes when userId or token changes
  useEffect(() => {
    if (token && userId) {
      loadNotes();
    } else {
      setNotes([]);
      setError(null);
    }
  }, [token, userId, loadNotes]);

  return {
    notes,
    isLoading,
    error,
    createNote,
    updateNote,
    deleteNote,
    refresh
  };
};

export default useUserNotes;